import { Switch, useToken } from '@chakra-ui/react';

type ToggleButtonType = {
  onClick?: any;
  title?: string;
  checked?: boolean;
  onCheckedChange?: any;
  name?: string;
  disabled?: boolean;
  onChange?: any;
  e2eTitle: string;
  /** Custom color token for the checked state (e.g., 'safety.medical.base') */
  checkedColor?: string;
};

/**
 * @useFor a single binary on/off switch with an inline label —
 *   typically inside a settings row or drawer form section.
 *
 *   Examples in-tree: notification preferences (Email / SMS / Push
 *   each as its own toggle), feature-flag overrides in admin UI,
 *   user "Active" / "Inactive" status in the edit drawer.
 *
 *   For multi-option pill-style toggles (Active / All / Archived)
 *   use `<TableOptions>` or `<ToggleGroup>`. For checkboxes within
 *   a multi-select context use `<StyledCheckbox>`. For a "yes/no"
 *   confirmation use `<Button>` with explicit labels — `<Toggle>`
 *   semantics are "state of a setting", not "trigger an action".
 */
const ToggleButton = (props: ToggleButtonType) => {
  const {
    onClick,
    title,
    checked = false,
    onCheckedChange,
    name,
    disabled = false,
    e2eTitle,
    onChange,
    checkedColor,
  } = props;

  // Resolve the Chakra theme color token (use custom color if provided)
  const colorToken = checkedColor || 'defaultSafety.primary';
  const [activeColor] = useToken('colors', [colorToken]);

  return (
    <Switch.Root
      size="md"
      disabled={disabled}
      name={name}
      checked={checked}
      onCheckedChange={onCheckedChange}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      cursor="pointer"
      onChange={onChange}
      data-e2e={e2eTitle}
    >
      <Switch.Label color="text.primary" fontSize="16px" fontWeight="500">
        {title}
      </Switch.Label>

      {/* 👇 Apply custom color when active */}
      <Switch.Control
        _checked={{
          bg: activeColor, // from theme
          borderColor: activeColor,
        }}
      />

      <Switch.HiddenInput />
    </Switch.Root>
  );
};

export default ToggleButton;
